import { DraftSectionItem } from '../../../components/Section/Section.model'
import {
  DraftConjointItem,
  DraftEntryResponseOption,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType
} from '../../../data/model/questionnaire'

// The Draft.js JSON stored alongside question/matrix/textcard text embeds
// the text itself inside its `blocks[].text` array. The cin-ui-components
// `EditorInput` renders from that JSON in preference to the plain `text`
// prop, which means a stale styling blob keeps showing the master text
// even when we swap `text` to the translation.
//
// `rewriteStylingText` walks the JSON, replaces every block's `text` with
// `nextText`, normalises ranges so they don't dangle past the new length,
// and returns the stringified result. If the JSON is missing or malformed
// we fall back to a fresh single-block document — the title still renders,
// just without inline formatting.
//
// It is only ever given a *translation's own* blob, never the master's: an
// inline style range is a pair of character offsets into the words it was
// authored against ("bold from 3 to 7"), and the translated words are a
// different length, so master ranges cannot be carried across. A translation
// with no blob of its own therefore renders unformatted.
export const buildPlainStyling = (text: string): string =>
  JSON.stringify({
    blocks: [
      {
        key: '1',
        text,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      }
    ],
    entityMap: {}
  })

const rewriteStylingText = (
  stylingJson: string | null | undefined,
  nextText: string
): string => {
  if (!stylingJson) return buildPlainStyling(nextText)
  try {
    const parsed = JSON.parse(stylingJson) as {
      blocks?: Array<{
        text?: string
        inlineStyleRanges?: { offset: number; length: number; style: string }[]
        entityRanges?: { offset: number; length: number; key: number }[]
        [k: string]: unknown
      }>
      entityMap?: Record<string, unknown>
    }
    const blocks = parsed.blocks ?? []
    if (blocks.length === 0) return buildPlainStyling(nextText)
    const clamp = (
      ranges: { offset: number; length: number }[] | undefined | null,
      max: number
    ): { offset: number; length: number }[] =>
      (ranges ?? [])
        .map((r) => ({
          ...r,
          offset: Math.min(r.offset, max),
          length: Math.min(r.length, Math.max(0, max - r.offset))
        }))
        .filter((r) => r.length > 0)
    // Put all of the new text in the first block; clear the rest so we don't
    // smear the translation across multiple paragraphs that no longer fit.
    const [first, ...rest] = blocks
    const newFirst = {
      ...first,
      text: nextText,
      inlineStyleRanges: clamp(first.inlineStyleRanges, nextText.length),
      entityRanges: clamp(first.entityRanges, nextText.length)
    }
    return JSON.stringify({
      ...parsed,
      blocks: [newFirst, ...rest.map((b) => ({ ...b, text: '' }))]
    })
  } catch {
    return buildPlainStyling(nextText)
  }
}

// Preview shows the survey as a respondent would get it, so an untranslated
// field is a hole — that is how the researcher sees what is still missing. The
// export instead falls back to the master, because a translator works from that
// document and needs the source text to translate.
const pick = (
  value: string | null | undefined,
  master: string,
  toMaster: boolean
): string => (value?.trim() ? value : toMaster ? master : '')

const overlayResponseOption = (
  option: DraftEntryResponseOption,
  languageCode: string,
  toMaster: boolean
): DraftEntryResponseOption => {
  const translation = option.responseOption?.translations?.find(
    (t) => t.language === languageCode
  )
  return {
    ...option,
    responseOption: option.responseOption
      ? {
          ...option.responseOption,
          value: pick(translation?.value, option.responseOption.value, toMaster)
        }
      : option.responseOption
  }
}

// Only the text is swapped; `language`/`country` describe the scale itself.
const overlayQuestionScale = (
  item: DraftQuestionItem,
  languageCode: string,
  toMaster: boolean
): DraftQuestionItem['questionScale'] => {
  const scale = item.questionScale
  if (!scale) return scale
  return {
    ...scale,
    labels: scale.labels.map((label) => ({
      ...label,
      label: {
        ...label.label,
        text: pick(
          label.labelTranslations?.find((t) => t.language === languageCode)
            ?.text,
          label.label.text,
          toMaster
        )
      }
    }))
  }
}

const overlayMaxDiffSpecification = (
  item: DraftQuestionItem,
  languageCode: string,
  toMaster: boolean
): DraftQuestionItem['maxDiffSpecification'] => {
  const spec = item.maxDiffSpecification
  if (!spec) return spec
  const translation = item.maxDiffLabelTranslations?.find(
    (t) => t.language === languageCode
  )
  const swap = <T extends { text: string }>(
    master: T,
    value: string | null | undefined
  ): T => ({ ...master, text: pick(value, master.text, toMaster) })
  return {
    ...spec,
    mostLabel: swap(spec.mostLabel, translation?.mostLabel),
    leastLabel: swap(spec.leastLabel, translation?.leastLabel),
    attributeLabel: swap(spec.attributeLabel, translation?.attributeLabel)
  }
}

const overlayQuestionItem = (
  item: DraftQuestionItem,
  languageCode: string,
  toMaster: boolean
): DraftQuestionItem => {
  const translation = item.question?.translations?.find(
    (t) => t.language === languageCode
  )
  const swappedOptions = item.responseOptions.map((opt) =>
    overlayResponseOption(opt, languageCode, toMaster)
  )
  const text = pick(translation?.text, item.question?.text ?? '', toMaster)
  return {
    ...item,
    responseOptions: swappedOptions,
    questionScale: overlayQuestionScale(item, languageCode, toMaster),
    maxDiffSpecification: overlayMaxDiffSpecification(
      item,
      languageCode,
      toMaster
    ),
    question: item.question
      ? {
          ...item.question,
          text,
          textStyling: rewriteStylingText(translation?.textStyling, text)
        }
      : item.question
  }
}

const overlayMatrixItem = (
  item: DraftMatrixItem,
  languageCode: string,
  toMaster: boolean
): DraftMatrixItem => {
  const titleTranslation = item.translations?.find(
    (t) => t.language === languageCode
  )
  const title = pick(
    titleTranslation?.title,
    item.matrixTitle?.title ?? '',
    toMaster
  )
  return {
    ...item,
    matrixTitle: item.matrixTitle
      ? {
          ...item.matrixTitle,
          title,
          titleStyling: rewriteStylingText(
            titleTranslation?.titleStyling,
            title
          )
        }
      : item.matrixTitle,
    matrixRows: item.matrixRows.map((row) => {
      if (!row.question) return row
      const rowTranslation = row.question.translations?.find(
        (t) => t.language === languageCode
      )
      const text = pick(rowTranslation?.text, row.question.text, toMaster)
      return {
        ...row,
        question: {
          ...row.question,
          text,
          textStyling: rewriteStylingText(rowTranslation?.textStyling, text)
        }
      }
    }),
    responseOptions: item.responseOptions.map((opt) =>
      overlayResponseOption(opt, languageCode, toMaster)
    )
  }
}

const overlayTextCardItem = (
  item: DraftTextCardItem,
  languageCode: string,
  toMaster: boolean
): DraftTextCardItem => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  const title = pick(translation?.title, item.textCard.title, toMaster)
  return {
    ...item,
    textCard: {
      ...item.textCard,
      title,
      subtitle: pick(translation?.subtitle, item.textCard.subtitle, toMaster),
      body: pick(translation?.body, item.textCard.body, toMaster),
      titleStyling: rewriteStylingText(translation?.titleStyling, title)
    }
  }
}

const overlayHighlighterItem = (
  item: DraftTextHighlighterItem,
  languageCode: string,
  toMaster: boolean
): DraftTextHighlighterItem => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  const swappedCategories = item.highlighterCategories.map((category) => ({
    ...category,
    responseOption: {
      ...category.responseOption,
      value: pick(
        category.responseOption.translations?.find(
          (t) => t.language === languageCode
        )?.value,
        category.responseOption.value,
        toMaster
      )
    }
  }))
  const text = pick(translation?.text, item.question?.text ?? '', toMaster)
  const contentText = pick(
    translation?.content.text,
    item.content.text,
    toMaster
  )
  return {
    ...item,
    highlighterCategories: swappedCategories,
    question: item.question
      ? {
          ...item.question,
          text,
          textStyling: rewriteStylingText(translation?.textStyling, text)
        }
      : item.question,
    content: {
      ...item.content,
      text: contentText,
      textStyling: rewriteStylingText(
        translation?.content.textStyling,
        contentText
      )
    }
  }
}

const overlayConjointItem = (
  item: DraftConjointItem,
  languageCode: string,
  toMaster: boolean
): DraftConjointItem => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  const swappedAttributes = item.attributes.map((attribute) => {
    const attributeTranslation = item.attributeTranslations?.find(
      (t) =>
        t.language === languageCode && t.attributeId === attribute.attributeId
    )
    return {
      ...attribute,
      name: pick(attributeTranslation?.name, attribute.name, toMaster),
      options: attribute.options.map((option) => ({
        ...option,
        value: pick(
          attributeTranslation?.options.find(
            (o) => o.attributeOptionId === option.attributeOptionId
          )?.value,
          option.value,
          toMaster
        )
      }))
    }
  })
  const text = pick(translation?.text, item.question?.text ?? '', toMaster)
  return {
    ...item,
    attributes: swappedAttributes,
    question: item.question
      ? {
          ...item.question,
          text,
          textStyling: rewriteStylingText(translation?.textStyling, text)
        }
      : item.question
  }
}

export interface TranslationOverlayOptions {
  // Untranslated fields render as the master text instead of empty. On for the
  // .docx export, whose reader is a translator working from the source text;
  // off for preview, which shows what a respondent would get.
  fallbackToMaster?: boolean
}

export const applyTranslationOverlay = (
  entry: DraftQuestionnaireEntry,
  languageCode: string,
  { fallbackToMaster = false }: TranslationOverlayOptions = {}
): DraftQuestionnaireEntry => {
  const toMaster = fallbackToMaster
  switch (entry.entryType) {
    // A section's questions live in `entryItem.entries`, not at top level, so
    // callers that walk the nested shape (the docx export) reach them only
    // through here. Without this case every in-section question exported in
    // the master language while the ones outside sections were translated.
    // Callers that pre-flatten (`flattenEntries` drops the section wrapper and
    // returns its children) never hit this branch.
    case EntryType.SectionEntryType: {
      const item = entry.entryItem as DraftSectionItem
      const titleTranslation = item.translations?.find(
        (t) => t.language === languageCode
      )
      return {
        ...entry,
        entryItem: {
          ...item,
          // Overlay the section's own title.
          title: pick(titleTranslation?.title, item.title, toMaster),
          // Guarded like `flattenEntries` does: the type says non-null but the
          // export swallows a throw here and would ship a doc with no questions
          // at all, so the runtime check is worth keeping.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          entries: (item.entries ?? []).map((child) =>
            applyTranslationOverlay(child, languageCode, { fallbackToMaster })
          )
        }
      } as DraftQuestionnaireEntry
    }
    case EntryType.QuestionEntryType:
      return {
        ...entry,
        entryItem: overlayQuestionItem(
          entry.entryItem as DraftQuestionItem,
          languageCode,
          toMaster
        )
      } as DraftQuestionnaireEntry
    case EntryType.MatrixEntryType:
      return {
        ...entry,
        entryItem: overlayMatrixItem(
          entry.entryItem as DraftMatrixItem,
          languageCode,
          toMaster
        )
      } as DraftQuestionnaireEntry
    case EntryType.TextCardEntryType:
      return {
        ...entry,
        entryItem: overlayTextCardItem(
          entry.entryItem as DraftTextCardItem,
          languageCode,
          toMaster
        )
      } as DraftQuestionnaireEntry
    case EntryType.TextHighlighterEntryType:
      return {
        ...entry,
        entryItem: overlayHighlighterItem(
          entry.entryItem as DraftTextHighlighterItem,
          languageCode,
          toMaster
        )
      } as DraftQuestionnaireEntry
    case EntryType.ConjointEntryType:
      return {
        ...entry,
        entryItem: overlayConjointItem(
          entry.entryItem as DraftConjointItem,
          languageCode,
          toMaster
        )
      } as DraftQuestionnaireEntry
    default:
      return entry
  }
}
